import React, { useEffect, useState } from "react";
import moment from "moment/moment";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  getPurchaseOrderByAdmin,
  getPurchaseOrderCsvByAdmin,
} from "src/api/purchaseOrder";
import { getLocale } from "react-i18nify";
import { Typography } from "@mui/material";
import { convertIntToCurrency } from "src/common";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDialog } from "src/context/DialogContext";
import FilterDialog from "src/components/purchase/FilterDialog";
import Stack from "@mui/material/Stack";
import Main from "src/components/core/Main";
import SearchInput from "src/components/purchase/SearchInput";
import FilterMobileButton from "src/components/purchase/FilterMobileButton";
import ExportCsvButton from "src/components/purchase/ExportCsvButton";
import DateSelected from "src/components/purchase/DateSelected";
import SellerSelected from "src/components/purchase/SellerSelected";
import StatusBar from "src/components/purchase/StatusBar";
import {
  Container,
  WrapperFilter,
  WrapperDataGrid,
  WrapperStatusRefund,
  WrapperMobileButton,
} from "../component";
import NoResultsOverlay from "../NoResultsOverlay";
import NoRowsOverlay from "../NoRowsOverlay";
import useFilterOrder from "../hooks/useFilterOrder";

function OrderList({ open }) {
  const [formatDataCsv, setFormatDataCsv] = useState([]);
  const { openDialog, closeDialog } = useDialog();
  const isSmallerThanTablet = useMediaQuery("(max-width:767px)");

  const config = {
    module: "purchase",
    initialState: {
      keyword: "",
      page: 0,
      page_size: 10,
      status: "all",
      date: null,
      seller: "",
      sortBy: "createdAt",
      orderCase: "desc",
    },
    initialVisibilityModel: {
      createdAt: true,
      orderNumber: !isSmallerThanTablet,
      customerName: true,
      sellerName: !isSmallerThanTablet,
      status: true,
      quantity: !isSmallerThanTablet,
      price: !isSmallerThanTablet,
      actions: true,
    },
    onFetch: getPurchaseOrderByAdmin,
    onFetchCsv: getPurchaseOrderCsvByAdmin,
    isSmallerThanTablet,
    dependencies: ["page", "date", "seller", "status", "sortBy", "orderCase"],
  };

  const {
    dataList,
    dataCsv,
    onSearchButton,
    handlePageChange,
    rowCount,
    statusCount,
    dataSearch,
    setDataSearch,
    isLoading,
    isLoadingCsv,
    columnVisibilityModel,
    handleFilterDialog,
  } = useFilterOrder(config);

  const columns = [
    {
      field: "createdAt",
      type: "number",
      flex: 1,
      minWidth: isSmallerThanTablet ? 92 : 112,
      editable: false,
      sortable: true,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "วันที่สั่งซื้อ" : "Purchase date",
      renderCell: (params) => {
        return (
          <div
            className="rowitem"
            style={{
              fontWeight: 400,
              fontSize: isSmallerThanTablet && "0.75rem",
            }}
          >
            {params?.row?.createdAt
              ? moment(params?.row?.createdAt).format("DD-MM-YYYY")
              : "-"}
          </div>
        );
      },
    },
    {
      field: "orderNumber",
      type: "number",
      flex: 1,
      minWidth: 160,
      editable: false,
      sortable: true,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            Order Number
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#6C6C70" }}
          >
            QT Number
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <div>
            <div
              className="rowitem"
              style={{
                fontWeight: 400,
                color: "#252525 ",
                fontSize: "0.875rem",
              }}
            >
              {params?.row?.orderNumber}
            </div>
            <div
              className="rowitem"
              style={{ fontWeight: 400, color: "#6C6C70", fontSize: "0.75rem" }}
            >
              {params?.row?.qtNumber ? params?.row?.qtNumber : "-"}
            </div>
          </div>
        );
      },
    },
    {
      field: "customerName",
      flex: 1,
      minWidth: isSmallerThanTablet ? 110 : 160,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
            }}
          >
            ชื่อลูกค้า
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: "#6C6C70",
              fontSize: isSmallerThanTablet ? "0.625rem" : "0.75rem",
            }}
          >
            {isSmallerThanTablet ? "ร้านค้า" : "เบอร์ติดต่อ"}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "start",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "24px 0px",
            }}
          >
            <div
              style={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <div
                className="MuiDataGrid-cellContent"
                style={{
                  fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row?.shippingAddress?.name
                  ? `${params.row?.shippingAddress?.name}`
                  : `-`}
              </div>
              {isSmallerThanTablet ? (
                <div
                  className="MuiDataGrid-cellContent"
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#6C6C70",
                  }}
                >
                  {params.row?.sellerName ? `${params.row?.sellerName}` : `-`}
                </div>
              ) : (
                <div
                  className="MuiDataGrid-cellContent"
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#6C6C70",
                  }}
                >
                  {params.row?.shippingAddress?.tel
                    ? `${params.row?.shippingAddress?.tel}`
                    : `-`}
                </div>
              )}
            </div>
          </Box>
        );
      },
    },
    {
      field: "sellerName",
      flex: 2,
      minWidth: 250,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            ร้านค้า
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#6C6C70" }}
          >
            สินค้า
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              fontSize="0.875rem"
              className="rowitem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.sellerName ? params?.row?.sellerName : "-"}
            </Typography>
            {params?.row?.productChoiceOrders?.map(
              ({ productChoiceOrder, color }, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: "8px",
                    color: "#6C6C70",
                    paddingLeft: "4px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box style={{ fontSize: "0.75rem", fontWeight: "400" }}>
                    •
                  </Box>
                  <Box
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "400",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${productChoiceOrder?.product?.item_name} ${color?.colorEn ? color?.colorEn : ""}`}
                  </Box>
                </Box>
              ),
            )}
          </Stack>
        );
      },
    },
    // {
    //   field: "quantity",
    //   flex: 1,
    //   minWidth: 80,
    //   editable: false,
    //   sortable: false,
    //   headerAlign: "center",
    //   align: "center",
    //   disableColumnMenu: true,
    //   headerName: getLocale() === "th" ? "จำนวน" : "quantity",
    //   renderCell: (params) => {
    //     return (
    //       <div className="rowitem">
    //         {params?.row?.productChoiceOrders?.length}
    //       </div>
    //     );
    //   },
    // },
    {
      field: "delivery",
      flex: 1,
      minWidth: 120,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "ขนส่ง" : "delivery",
      renderCell: (params) => {
        const { deliType } = params?.row || {};
        const carrier = deliType === "lalamove-xsf" ? "Lalamove ส่งฟรี" : "-";
        return <div className="rowitem">{carrier}</div>;
      },
    },
    {
      field: "price",
      flex: 1,
      minWidth: 85,
      editable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "ราคา" : "price",
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {convertIntToCurrency(params?.row?.totalAll)}
          </div>
        );
      },
    },
    {
      field: "status",
      headerClassName: "status-header",
      flex: 1,
      minWidth: isSmallerThanTablet ? 48 : 120,
      editable: false,
      sortable: false,
      headerAlign: isSmallerThanTablet ? "center" : "left",
      align: isSmallerThanTablet ? "center" : "left",
      flexDirection: "column",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "สถานะ" : "Status",
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              margin: "8px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: isSmallerThanTablet ? "center" : "start",
              outline: "none",
              whiteSpace: "nowrap",
            }}
          >
            <Stack
              direction="row"
              alignItems={"center"}
              justifyContent={"start"}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: statusTabs.find(
                    (item) => item?.label === params?.row?.statusTh,
                  )?.color,
                  marginRight: 1,
                }}
              />

              {!isSmallerThanTablet && (
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    maxWidth: "100px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {params?.row?.statusTh}
                </Typography>
              )}
            </Stack>

            {params.row.status_refund === "refund" ? (
              <WrapperStatusRefund>
                <span
                  style={{
                    color: "#E13B30",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์มีการคืนเงิน
                </span>
              </WrapperStatusRefund>
            ) : null}

            {params.row.status_refund === "wait refund" ? (
              <WrapperStatusRefund>
                <ErrorIcon
                  sx={{
                    color: "#E13B30",
                  }}
                />

                <span
                  style={{
                    color: "#E13B30",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์รอการคืนเงิน
                </span>
              </WrapperStatusRefund>
            ) : null}

            {params.row.status_refund === "succ refund" ? (
              <WrapperStatusRefund>
                <CheckCircleIcon
                  sx={{
                    color: "#3CB11E",
                  }}
                />

                <span
                  style={{
                    color: "#6A665A",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์คืนเงินสำเร็จ
                </span>
              </WrapperStatusRefund>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "ดำเนินการ",
      type: "actions",
      flex: 1,
      minWidth: isSmallerThanTablet ? 48 : 68,
      sortable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="รายละเอียด"
          onClick={() =>
            window.open(`/order/detail/${params.row._id}`, "_blank")
          }
          showInMenu
        />,
      ],
    },
  ];

  const statusTabs = [
    {
      label: "ทั้งหมด",
      count: statusCount?.all || 0,
      color: "",
      value: "all",
    },
    {
      label: "รอจ่ายเงิน",
      count: statusCount?.waitingPayment || 0,
      color: "#9A66ED",
      value: "wp",
    },
    {
      label: "รอดำเนินการ",
      count: statusCount?.inProgress || 0,
      color: "#FBB634",
      value: "inProgress",
    },
    {
      label: "สั่งซื้อสำเร็จ",
      count: statusCount?.ordered || 0,
      color: "#219653",
      value: "ordered",
    },
  ];

  const handleOpenDialog = () => {
    openDialog({
      type: "custom",
      customDialog: (
        <FilterDialog
          isFilterSeller={true}
          dataSearch={dataSearch}
          onClose={() => closeDialog()}
          onConfirm={handleFilterDialog}
          dateLabel="วันที่สั่งซื้อ"
        />
      ),
    });
  };

  const handleColumnClick = ({ field }) => {
    if (field === "orderNumber" || field === "createdAt") {
      setDataSearch((prev) => ({
        ...prev,
        sortBy: field,
        orderCase: dataSearch.orderCase === "asc" ? "desc" : "asc",
        page: 0,
      }));
    }
  };

  useEffect(() => {
    if (dataCsv.length > 0) {
      const format = dataCsv.map((item, index) => {
        const {
          createdAt,
          sellerName,
          orderNumber,
          statusTh,
          refPayNo,
          productChoiceOrders,
          totalAll,
          shippingAddress,
          statusPayment,
          statusPaymentRePay,
        } = item;

        return {
          ลำดับที่: index + 1,
          วันที่สั่งซื้อ: moment(createdAt).format("DD-MM-YYYY"),
          หมายเลขออเดอร์: orderNumber ? orderNumber : "-",
          ชื่อร้านค้า: sellerName ? sellerName : "-",
          ราคา: totalAll ? convertIntToCurrency(totalAll) : "-",
          สถานะ: statusTh ? statusTh : "-",
          จำนวนสินค้า: productChoiceOrders.length
            ? productChoiceOrders.length
            : "-",
          หมายเลขอ้างอิง: refPayNo ? refPayNo : "-",
          รายการสินค้า: productChoiceOrders.map(
            (item) => `•${item?.productChoiceOrder?.product?.item_name}\n`,
          ),
          ชื่อลูกค้า: shippingAddress?.name ? shippingAddress?.name : "-",
          ที่อยู่: shippingAddress?.address ? shippingAddress?.address : "-",
          จังหวัด: shippingAddress?.province ? shippingAddress?.province : "-",
          อำเภอ: shippingAddress?.district ? shippingAddress?.district : "-",
          ตำบล: shippingAddress?.subDistrict
            ? shippingAddress?.subDistrict
            : "-",
          เลขไปรษณีย์: shippingAddress?.zipCode
            ? shippingAddress?.zipCode
            : "-",
          เบอร์ติดต่อ: shippingAddress?.tel ? shippingAddress?.tel : "-",
          ใบเสร็จ1: statusPayment?.paySlipImage?.docUrl
            ? statusPayment?.paySlipImage?.docUrl
            : "-",
          ใบเสร็จ2: statusPaymentRePay?.paySlipOtherImage?.docUrl
            ? statusPaymentRePay?.paySlipOtherImage?.docUrl
            : "-",
        };
      });

      setFormatDataCsv(format);
    } else {
      setFormatDataCsv([]);
    }
  }, [dataCsv]);

  return (
    <Main open={open}>
      <Container>
        <WrapperFilter>
          <SearchInput
            onSearch={onSearchButton}
            setDataSearch={setDataSearch}
          />

          <WrapperMobileButton isSmallerThanTablet={isSmallerThanTablet}>
            <FilterMobileButton
              onDialog={handleOpenDialog}
              dataSearch={dataSearch}
            />

            <ExportCsvButton
              dataList={dataCsv}
              formatDataCsv={formatDataCsv}
              isLoading={isLoadingCsv}
              isSmallerThanTablet={isSmallerThanTablet}
              topic="รายการสั่งซื้อสินค้า"
              mobile
            />
          </WrapperMobileButton>

          <DateSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dateLabel={"วันที่สั่งซื้อ"}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <SellerSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <ExportCsvButton
            dataList={dataCsv}
            formatDataCsv={formatDataCsv}
            isLoading={isLoadingCsv}
            isSmallerThanTablet={isSmallerThanTablet}
            topic="รายการสั่งซื้อสินค้า"
          />
        </WrapperFilter>

        <StatusBar
          dataSearch={dataSearch}
          setDataSearch={setDataSearch}
          statusTabs={statusTabs}
          isSmallerThanTablet={isSmallerThanTablet}
        />

        <WrapperDataGrid>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowX: "scroll",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <DataGrid
              rows={dataList}
              getRowId={(row) => row._id}
              getRowHeight={() => "auto"}
              loading={isLoading}
              columns={columns}
              pageSize={10}
              rowCount={rowCount}
              paginationMode="server"
              page={dataSearch.page}
              onPageChange={(e) => handlePageChange(e)}
              rowsPerPageOptions={[10]}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay,
                NoResultsOverlay,
              }}
              componentsProps={{
                basePopper: {},
              }}
              sx={{
                "& .MuiDataGrid-actionsCell": {
                  transform: "rotate(90deg)",
                },
                "&. MuiDataGrid-columnHeaderTitleContainerContent": {
                  lineHeight: "normal",
                },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                },

                "& .status-header": {
                  padding: 0,
                },
              }}
              disableColumnResize={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnHeaderClick={handleColumnClick}
            />
          </Box>
        </WrapperDataGrid>
      </Container>
    </Main>
  );
}

export default OrderList;
